<template>
  <!-- <div>  -->
  <eg-modal
    id="add-eg-workspace-modal"
    :title="$t('dashboard.workspace.organize')"
    size="large"
    :closable="true"
    @close="handleModalClose"
    @open="loadProgress"
  >
    <!-- own Header with Subtitle -->
    <template #header="{ close }">
      <eg-modal-header
        :title="$t('dashboard.workspace.organize')"
        closable
        @onClose="handleCancel(close)"
      >
        <template #title="{ title }">
          <div class="d-flex flex-column">
            <div class="d-flex align-items-center">
              <h3 class="semi-bold eg-modal__title">{{ title }}</h3>
            </div>
          </div>
        </template>
      </eg-modal-header>
    </template>

    <!-- Modal Body -->
    <div class="workspace-selection p-4">
      <!-- Workspace Options Grid -->
      <div class="workspace-selection__grid">
        <!-- Yearly Based Option -->
        <div
          class="workspace-option"
          :class="{ 'workspace-option--selected': selectedType === 'yearly' }"
          @click="selectWorkspaceType('yearly')"
        >
          <!-- Yearly Illustration -->
          <div
            class="workspace-option__illustration d-flex align-items-center justify-content-center"
          >
            <img src="@/assets/workflow/yearly.svg" alt="Yearly workflow" />
          </div>

          <!-- Option Content -->
          <div class="workspace-option__content">
            <h3 class="workspace-option__title">
              {{ $t(workspaceTypes.yearly.title) }}
            </h3>
            <p class="workspace-option__description">
              {{ $t(workspaceTypes.yearly.description) }}
            </p>
          </div>
        </div>

        <!-- Class Based Option -->
        <div
          class="workspace-option"
          :class="{ 'workspace-option--selected': selectedType === 'class' }"
          @click="selectWorkspaceType('class')"
        >
          <!-- Class Illustration -->
          <div
            class="workspace-option__illustration d-flex align-items-center justify-content-center"
          >
            <img src="@/assets/workflow/class.svg" alt="Class workflow" />
          </div>

          <!-- Option Content -->
          <div class="workspace-option__content">
            <h3 class="workspace-option__title">
              {{ $t(workspaceTypes.class.title) }}
            </h3>
            <p class="workspace-option__description">
              {{ $t(workspaceTypes.class.description) }}
            </p>
          </div>
        </div>

        <!-- Subject Based Option -->
        <div
          class="workspace-option"
          :class="{
            'workspace-option--selected': selectedType === 'subject',
          }"
          @click="selectWorkspaceType('subject')"
        >
          <!-- Subject Illustration -->
          <div
            class="workspace-option__illustration d-flex align-items-center justify-content-center"
          >
            <img src="@/assets/workflow/own.svg" alt="Subject workflow" />
          </div>

          <!-- Option Content -->
          <div class="workspace-option__content">
            <h3 class="workspace-option__title">
              {{ $t(workspaceTypes.subject.title) }}
            </h3>
            <p class="workspace-option__description">
              {{ $t(workspaceTypes.subject.description) }}
            </p>
          </div>
        </div>

        <!-- Create Your Own Option -->
        <div
          class="workspace-option"
          :class="{ 'workspace-option--selected': selectedType === 'own' }"
          @click="selectWorkspaceType('own')"
        >
          <!-- own Illustration -->
          <div
            class="workspace-option__illustration d-flex align-items-center justify-content-center"
          >
            <img src="@/assets/workflow/subject.svg" alt="own workflow" />
          </div>

          <!-- Option Content -->
          <div class="workspace-option__content">
            <h3 class="workspace-option__title">
              {{ $t(workspaceTypes.own.title) }}
            </h3>
            <p class="workspace-option__description">
              {{ $t(workspaceTypes.own.description) }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Footer -->
    <template #footer="{ close }">
      <div class="workspace-selection__footer d-flex justify-content-end">
        <eg-button type="ordinary" size="medium" @click="handleCancel(close)">
          {{ $t("common.cancel") }}
        </eg-button>
        <eg-button
          type="primary"
          size="medium"
          :disabled="!selectedType"
          @click="handleContinue"
        >
          {{ $t("common.continue") }}
        </eg-button>
      </div>
    </template>
  </eg-modal>
</template>

<script>
import WorkspaceConfiguration from "./WorkspaceConfiguration.vue";
import { WORKSPACE_TYPES } from "@/constants";
import EgModalHeader from "@/components/global/eg/modals/eg_modal_header.vue";

export default {
  name: "WorkspaceSelectionModal",

  components: { EgModalHeader },

  inject: ["workspacesProvider"],

  emits: ["workspace-selected", "workspace-created", "cancel"],

  data() {
    return {
      currentStep: 1,
      selectedType: null,
      workspaceProgress: {
        currentStep: 1,
        totalSteps: 3,
        selectedWorkspaceType: null,
        configurations: {
          yearly: null,
          class: null,
          subject: null,
          own: null,
        },
      },
    };
  },
  computed: {
    workspaceTypes() {
      return WORKSPACE_TYPES;
    },
  },

  methods: {
    selectWorkspaceType(type) {
      this.selectedType = type;
      this.workspaceProgress.selectedWorkspaceType = type;
      this.saveProgress();
    },

    handleContinue() {
      if (!this.selectedType) return;

      // Save current selection
      this.workspaceProgress.currentStep = 2;
      this.workspaceProgress.selectedWorkspaceType = this.selectedType;
      this.saveProgress();

      // Always navigate to step 2 (configuration) for all types
      this.openWorkspaceConfigurationModal();
    },

    handleCancel(
      closeModal = () => this.$egModal.hide("add-eg-workspace-modal")
    ) {
      // Preserve progress on cancel to remember the last step
      this.saveProgress();
      this.$emit("cancel");
      closeModal();
    },

    handleBackFromStep2(payload) {
      // Cache in-progress configuration for current type (if provided)
      if (payload && payload.type && payload.configuration) {
        this.workspaceProgress.configurations[payload.type] =
          payload.configuration;
        this.saveProgress();
      }
      this.currentStep = 1;
      this.workspaceProgress.currentStep = 1;
      this.saveProgress();
    },

    handleWorkspaceCreated(data) {
      // Save configuration and emit completion
      this.workspaceProgress.configurations[data.type] = data.configuration;
      this.workspaceProgress.currentStep = 3;
      this.saveProgress();
      this.$egModal.hide("add-eg-workspace-modal");

      // Emit final completion
      this.$emit("workspace-created", {
        type: data.type,
        configuration: data.configuration,
        progress: this.workspaceProgress,
      });

      this.workspacesProvider.addFolderWhere(
        "essaygrader",
        () => false, // all false means add to root
        data.configuration
      );

      this.$router.push({
        name: "workspace-essay-upload",
        params: {
          workspaceKey: "essaygrader",
          id:
            data.configuration[0].children[0]?.children?.[0]?.id ||
            data.configuration[0].children[0]?.id,
        },
      });

      // Do not auto-close; leave modal open per requirement
    },

    handleModalClose(value) {
      // Save progress when modal is closed but not cancelled
      if (!value) {
        this.saveProgress();
      }
    },

    openWorkspaceConfigurationModal() {
      this.$egModal.show("workspace-configuration-modal", {
        component: WorkspaceConfiguration,
        props: {
          workspaceType: this.selectedType,
          initialStructure:
            this.workspaceProgress.configurations[this.selectedType] || null,
        },
        on: {
          "workspace-created": (data) => this.handleWorkspaceCreated(data),
          back: (payload) => this.handleBackFromStep2(payload),
          cancel: () => this.handleCancel(),
        },
      });
    },

    saveProgress() {
      // Save to localStorage to persist across sessions
      const progressKey = "workspace_creation_progress";
      localStorage.setItem(progressKey, JSON.stringify(this.workspaceProgress));
    },

    loadProgress() {
      // Load existing progress from localStorage
      const progressKey = "workspace_creation_progress";
      const savedProgress = localStorage.getItem(progressKey);

      if (savedProgress) {
        try {
          this.workspaceProgress = JSON.parse(savedProgress);
          this.selectedType = this.workspaceProgress.selectedWorkspaceType;
          this.currentStep = this.workspaceProgress.currentStep;

          // TODO: restore step 2 state if needed
          // if (this.currentStep === 2) {
          //   this.openWorkspaceConfigurationModal();
          // }
        } catch (error) {
          console.error("Error loading workspace progress:", error);
          this.clearProgress();
        }
      }
    },

    clearProgress() {
      // Clear all saved progress
      const progressKey = "workspace_creation_progress";
      localStorage.removeItem(progressKey);
      this.workspaceProgress = {
        currentStep: 1,
        totalSteps: 3,
        selectedWorkspaceType: null,
        configurations: {
          yearly: null,
          class: null,
          subject: null,
          own: null,
        },
      };
      this.selectedType = null;
      this.currentStep = 1;
      // this.showSubjectConfig = false;
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .eg-modal__content {
  .eg-modal__header {
    padding: 24px;
    border-bottom: 1px solid var(--color-stroke-neutral-default, #e6e6e6);
    margin-bottom: 0;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }
  .eg-modal__body {
    padding: 0;
  }
}

.workspace-header {
  &__title {
    color: var(--color-text-neutral-on-white);
    font-size: 20px;
    font-weight: 600;
    line-height: 28px;
    margin: 0 0 8px 0;
    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      sans-serif;
  }

  &__subtitle {
    color: var(--color-text-neutral-on-white);
    font-size: 14px;
    line-height: 20px;
    margin: 0;
    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      sans-serif;
  }
}

.workspace-selection {
  &__grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }

  &__footer {
    gap: 12px;
  }
}

.workspace-option {
  border: 1px solid var(--color-stroke-neutral-default, #e6e6e6);
  border-radius: 12px;
  padding: 0;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  background: white;
  overflow: hidden;
  height: 280px;
  width: 209px;

  &:hover {
    border-color: var(--color-stroke-primary);
    box-shadow: 0 4px 12px rgba(138, 63, 252, 0.12);
  }

  &--selected {
    border-color: var(--color-stroke-primary);
    box-shadow: 0 4px 16px rgba(138, 63, 252, 0.16);
  }

  &__illustration {
    height: 136px;
    margin: 0;
    background: var(--color-surface-neutral-container-1, #f8fafc);
    border-radius: 0;
    padding: 24px;

    svg {
      max-width: 100%;
      width: auto;
      height: auto;
    }
  }

  &__content {
    text-align: center;
    padding: 24px;
  }

  &__title {
    color: var(--color-text-neutral-on-white);
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    margin: 0 0 8px 0;
    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      sans-serif;
  }

  &__description {
    color: var(--color-text-secondary);
    font-size: 14px;
    line-height: 20px;
    margin: 0;
  }
}

// Remove old placeholder styles as they're no longer needed
.illustration-placeholder,
.folder-structure,
.folder {
  display: none;
}

// Responsive adjustments
@media (max-width: 1200px) {
  .workspace-selection__grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
}

@media (max-width: 768px) {
  .workspace-selection__grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .workspace-option {
    &__illustration {
      height: 120px;
      padding: 24px;
    }

    &__content {
      padding: 16px 24px 24px 24px;
    }
  }
}
</style>
